import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../../state/editorStore';
import {
  WEIGHTS, IN_KINDS, IDLES, hex, secs, pct, compact,
  PhaseControls, nearestWeight, FontSelect, Row, Slider, Seg, Toggle,
} from './controls';
import { api } from '../../api/client';
import { playbackClock } from '../../playback/clock';
import {
  DEFAULT_TEXTURE,
  HOOK_MOTION_PRESETS,
  MOTION_KIND_DEFAULTS,
  resolveHookMotion,
  syncLinesToSpeech,
  type HookExitKind,
  type HookLineStyle,
  type HookMoment,
  type HookMotion,
  type HookMotionKind,
  type HookPhase,
  type HookSplit,
} from '@captionflow/caption-engine';

/**
 * Right-side inspector for the SELECTED hook (only while the Hooks tab is open).
 * The left panel picks the hook + a style preset; this panel fine-tunes it in
 * four tabs - Text (lines, each with its own look + reveal time), Style (fill,
 * colour, outline, glow, footage texture), Motion (entrance / exit / cascade /
 * hold) and Place (timing, position, depth, head tracking). Writes the
 * project's hooks list (live preview + export).
 */
type Tab = 'text' | 'style' | 'motion' | 'place';

const ANCHORS: { v: HookMoment['anchor']; label: string }[] = [
  { v: 'upper', label: 'Upper' },
  { v: 'center', label: 'Center' },
  { v: 'lower', label: 'Lower' },
];
const TYPES: { v: HookMoment['type']; label: string }[] = [
  { v: 'power', label: 'Power word' },
  { v: 'opening', label: 'Opening' },
];
const FILLS: { v: NonNullable<HookMoment['fill']>; label: string }[] = [
  { v: 'solid', label: 'Solid' },
  { v: 'metal', label: 'Metal' },
  { v: 'footage', label: 'Footage' },
  { v: 'shine', label: 'Shine' },
  { v: 'flow', label: 'Flow' },
  { v: 'wipe', label: 'Wipe' },
];
const CASES: { v: NonNullable<HookMoment['textCase']>; label: string }[] = [
  { v: 'upper', label: 'AA' },
  { v: 'asis', label: 'Aa' },
  { v: 'lower', label: 'aa' },
];
const ALIGNS: { v: NonNullable<HookMoment['align']>; label: string }[] = [
  { v: 'left', label: 'Left' },
  { v: 'center', label: 'Center' },
  { v: 'right', label: 'Right' },
];
const OUT_KINDS: { v: HookExitKind; label: string }[] = [
  { v: 'mirror', label: 'Reverse' },
  { v: 'fade', label: 'Fade' },
  { v: 'slideUp', label: 'Up' },
  { v: 'slideDown', label: 'Down' },
  { v: 'slideLeft', label: 'Left' },
  { v: 'slideRight', label: 'Right' },
  { v: 'mask', label: 'Mask' },
  { v: 'blur', label: 'Blur' },
  { v: 'zoomIn', label: 'Shrink' },
  { v: 'zoomOut', label: 'Grow' },
  { v: 'none', label: 'Cut' },
];
const SPLITS: { v: HookSplit; label: string }[] = [
  { v: 'block', label: 'All' },
  { v: 'line', label: 'Line' },
  { v: 'word', label: 'Word' },
  { v: 'char', label: 'Letter' },
];
export function StudioHookInspector() {
  const leftTab = useEditor((s) => s.leftTab);
  const hooks = useEditor((s) => s.hooks);
  const words = useEditor((s) => s.words);
  const selectedId = useEditor((s) => s.selectedHookId);
  const durationMs = useEditor((s) => s.durationMs);
  const projectId = useEditor((s) => s.projectId);
  const updateHook = useEditor((s) => s.updateHook);
  const removeHook = useEditor((s) => s.removeHook);
  const selectHook = useEditor((s) => s.selectHook);
  const cutout = useEditor((s) => s.cutout);
  const setCutout = useEditor((s) => s.setCutout);
  const setDepthDialogOpen = useEditor((s) => s.setDepthDialogOpen);
  const [tab, setTab] = useState<Tab>('text');
  const [openLine, setOpenLine] = useState<number | null>(0);
  const [tracking, setTracking] = useState(false);
  const stopAt = useRef<(() => void) | null>(null);

  // Stop any pending "replay" watcher when the inspector goes away.
  useEffect(() => () => stopAt.current?.(), []);

  const h = hooks.find((x) => x.id === selectedId) ?? null;
  // Only in the Hooks tab, and only when a hook is selected.
  if (leftTab !== 'takeover' || !h) return null;

  const hasOpening = hooks.some((x) => x.type === 'opening' && x.id !== h.id);
  const dur = Math.max(durationMs, hooks.at(-1)?.endMs ?? 0, 1000);
  const span = h.endMs - h.startMs;
  const pillOn = Boolean(h.bgColor);
  const strokeOn = Boolean(h.stroke && h.stroke.width > 0);
  const glowOn = Boolean(h.glow);
  const lines = h.text.split('\n').map((l) => l.trim()).filter(Boolean);
  const multi = h.text.includes('\n') && lines.length > 1;
  const motion = resolveHookMotion(h);
  const tex = { ...DEFAULT_TEXTURE, ...(h.texture ?? {}) };
  const usesFootage = h.fill === 'footage' || Boolean(h.lineStyles?.some((l) => l?.fill === 'footage'));

  const set = (patch: Partial<Omit<HookMoment, 'id'>>) => updateHook(h.id, patch);
  const setMotion = (patch: Partial<HookMotion>) => set({ motion: { ...motion, ...patch } });
  const setIn = (patch: Partial<HookPhase>) => setMotion({ in: { ...motion.in, ...patch } });
  const setOut = (patch: Partial<HookPhase<HookExitKind>>) => setMotion({ out: { ...motion.out, ...patch } });
  const setLine = (i: number, patch: Partial<HookLineStyle>) => {
    const arr = [...(h.lineStyles ?? [])];
    while (arr.length <= i) arr.push(null);
    const next = compact({ ...(arr[i] ?? {}), ...patch });
    arr[i] = Object.keys(next).length ? next : null;
    while (arr.length && arr[arr.length - 1] == null) arr.pop();
    set({ lineStyles: arr.length ? arr : undefined });
  };
  const setTexture = (patch: Partial<typeof tex>) => set({ texture: { ...tex, ...patch } });

  const toggleBehind = (next: boolean) => {
    set({ behind: next });
    if (next && cutout.status !== 'ready') setDepthDialogOpen(true);
  };
  const setFill = (v: NonNullable<HookMoment['fill']>) => {
    // Flow needs a gradient to scroll; seed a default if none set yet.
    if (v === 'flow' && !h.gradient) set({ fill: v, gradient: { from: '#a78bfa', to: '#22d3ee', angleDeg: 0 } });
    else if (v === 'footage' && !h.texture) set({ fill: v, texture: { ...DEFAULT_TEXTURE } });
    else if (v === 'metal' && (!h.color || h.color === '#ffffff')) set({ fill: v, color: '#d9a520' });
    else set({ fill: v });
  };

  /** Play just this hook so motion tweaks can be judged immediately. */
  const replay = () => {
    stopAt.current?.();
    playbackClock.seek(Math.max(0, h.startMs - 150));
    playbackClock.play();
    const end = h.endMs + 200;
    let done = false;
    const unsub = playbackClock.subscribe((t) => {
      // pause() notifies listeners synchronously - unsubscribe first.
      if (done || t < end) return;
      done = true;
      unsub();
      stopAt.current = null;
      playbackClock.pause();
    });
    stopAt.current = unsub;
  };

  const pickInKind = (kind: HookMotionKind) =>
    setIn({ kind, ...MOTION_KIND_DEFAULTS[kind], durationMs: kind === 'none' ? 0 : Math.max(200, motion.in.durationMs) });
  const pickOutKind = (kind: HookExitKind) =>
    setOut(
      kind === 'mirror'
        ? { kind, blur: motion.in.blur, easing: motion.in.easing, durationMs: Math.max(200, motion.out.durationMs) }
        : { kind, ...MOTION_KIND_DEFAULTS[kind], durationMs: kind === 'none' ? 0 : Math.max(200, motion.out.durationMs) },
    );

  const syncToSpeech = () => {
    const delays = syncLinesToSpeech(h, words);
    const arr = [...(h.lineStyles ?? [])];
    delays.forEach((d, i) => {
      while (arr.length <= i) arr.push(null);
      arr[i] = { ...(arr[i] ?? {}), delayMs: d };
    });
    set({
      lineStyles: arr,
      // A build needs a visible entrance per line.
      motion: motion.in.kind === 'none' ? { ...motion, in: { kind: 'slideUp', durationMs: 450, ...MOTION_KIND_DEFAULTS.slideUp, blur: 0.5, distance: 0.05 } } : motion,
    });
  };
  const clearLineTiming = () =>
    set({
      lineStyles: (h.lineStyles ?? []).map((l) => {
        if (!l) return null;
        const { delayMs: _d, ...rest } = l;
        return Object.keys(rest).length ? rest : null;
      }),
    });

  /** Turn head-tracking on: needs the cut-out, then a (cheap) track pass. */
  const toggleTrack = async (on: boolean) => {
    if (!on) return set({ track: undefined });
    set({ track: { strength: 1, scale: true } });
    if (cutout.status !== 'ready') {
      setDepthDialogOpen(true);
      return;
    }
    if (cutout.track || !projectId) return;
    setTracking(true);
    try {
      await api.startTrack(projectId);
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const c = await api.getCutout(projectId);
        if (c.track) {
          setCutout({ track: c.track });
          break;
        }
      }
    } catch {
      /* surfaced by the hint below */
    } finally {
      setTracking(false);
    }
  };

  const motionMatches = (m: HookMotion) => JSON.stringify(m) === JSON.stringify(motion);

  return (
    <aside className="studio-right shk">
      <div className="si-head">
        <span className="si-kicker">HOOK{h.type === 'opening' ? ' · OPENING' : ''}</span>
        <div className="si-word-row">
          <textarea
            className="si-word shk-text"
            key={h.id}
            defaultValue={h.text}
            rows={Math.min(5, Math.max(1, h.text.split('\n').length))}
            onChange={(e) => {
              if (e.target.value.trim()) set({ text: e.target.value.slice(0, 300) });
            }}
            placeholder="HOOK"
          />
          <button className="si-x" onClick={() => selectHook(null)} aria-label="Close">✕</button>
        </div>
        <p className="shk-hint">Press Enter for a new line - each line gets its own style and timing.</p>
        <div className="shk-subtabs shk-insp-tabs">
          {(['text', 'style', 'motion', 'place'] as const).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t[0]!.toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="si-scroll shk-fields">
        {tab === 'text' && (
          <>
            <Row label="Font">
              <FontSelect value={h.fontFamily ?? 'Inter'} onChange={(v) => set({ fontFamily: v })} />
            </Row>
            <Row label="Weight">
              <Seg options={WEIGHTS} value={String(nearestWeight(h.fontWeight ?? 900))} onPick={(v) => set({ fontWeight: Number(v) })} />
            </Row>
            <Row label="Italic">
              <Toggle on={Boolean(h.italic)} onClick={() => set({ italic: !h.italic || undefined })} />
            </Row>
            <Row label="Case">
              <Seg options={CASES} value={h.textCase ?? 'upper'} onPick={(v) => set({ textCase: v === 'upper' ? undefined : v })} />
            </Row>
            <Row label="Align">
              <Seg options={ALIGNS} value={h.align ?? 'center'} onPick={(v) => set({ align: v === 'center' ? undefined : v })} />
            </Row>
            <Slider label="Letter spacing" min={-0.1} max={0.6} step={0.01} value={h.tracking ?? 0}
              onChange={(v) => set({ tracking: Math.abs(v) < 0.005 ? undefined : v })} display={`${Math.round((h.tracking ?? 0) * 100)}`} />
            <Slider label="Line spacing" min={0.6} max={1.6} step={0.02} value={h.lineHeight ?? 1.06}
              onChange={(v) => set({ lineHeight: v })} display={(h.lineHeight ?? 1.06).toFixed(2)} />

            <div className="ses-divider" />
            <span className="str-section">Lines{multi ? ` · ${lines.length}` : ''}</span>
            {!multi ? (
              <p className="shk-hint">Add line breaks to the text to style each line on its own (e.g. a thin kicker over a huge word) and build them in as they're spoken.</p>
            ) : (
              <>
                <div className="shk-line-actions">
                  <button className="shk-mini" onClick={syncToSpeech}>Sync lines to speech</button>
                  <button className="shk-mini ghost" onClick={clearLineTiming}>Clear timing</button>
                </div>
                {lines.map((text, i) => {
                  const ls = h.lineStyles?.[i] ?? {};
                  const open = openLine === i;
                  return (
                    <div key={i} className={open ? 'shk-line on' : 'shk-line'}>
                      <button className="shk-line-head" onClick={() => setOpenLine(open ? null : i)}>
                        <span className="shk-line-n">{i + 1}</span>
                        <span className="shk-line-t" style={{ fontFamily: ls.fontFamily ?? h.fontFamily, fontWeight: ls.fontWeight ?? h.fontWeight ?? 900, fontStyle: ls.italic ?? h.italic ? 'italic' : undefined }}>
                          {text}
                        </span>
                        {ls.color && <span className="shk-line-dot" style={{ background: ls.color }} />}
                        {ls.delayMs != null && <span className="shk-line-d">+{secs(ls.delayMs)}</span>}
                        <span className="shk-line-c">{open ? '−' : '+'}</span>
                      </button>
                      {open && (
                        <div className="shk-line-body">
                          <Row label="Font">
                            <FontSelect value={ls.fontFamily ?? ''} inherit onChange={(v) => setLine(i, { fontFamily: v || undefined })} />
                          </Row>
                          <Row label="Weight">
                            <Seg options={[{ v: '', label: '—' }, ...WEIGHTS]} value={ls.fontWeight ? String(nearestWeight(ls.fontWeight)) : ''}
                              onPick={(v) => setLine(i, { fontWeight: v ? Number(v) : undefined })} />
                          </Row>
                          <Row label="Italic">
                            <Seg options={[{ v: '', label: 'Same' }, { v: 'on', label: 'Italic' }, { v: 'off', label: 'Upright' }]}
                              value={ls.italic == null ? '' : ls.italic ? 'on' : 'off'}
                              onPick={(v) => setLine(i, { italic: v === '' ? undefined : v === 'on' })} />
                          </Row>
                          <Slider label="Size" min={0.25} max={2.5} step={0.05} value={ls.size ?? 1}
                            onChange={(v) => setLine(i, { size: Math.abs(v - 1) < 0.01 ? undefined : v })} display={pct(ls.size ?? 1)} />
                          <Row label="Own color">
                            <div className="shk-inline">
                              <Toggle on={ls.color != null} onClick={() => setLine(i, { color: ls.color != null ? undefined : '#ffd400' })} />
                              {ls.color != null && (
                                <input type="color" className="shk-color" value={hex(ls.color, '#ffd400')} onChange={(e) => setLine(i, { color: e.target.value })} />
                              )}
                            </div>
                          </Row>
                          <Row label="Fill">
                            <Seg options={[{ v: '', label: 'Same' }, { v: 'solid', label: 'Solid' }, { v: 'metal', label: 'Metal' }, { v: 'footage', label: 'Footage' }]}
                              value={ls.fill ?? ''} onPick={(v) => setLine(i, { fill: (v || undefined) as HookLineStyle['fill'] })} wrap />
                          </Row>
                          <Row label="Case">
                            <Seg options={[{ v: '', label: 'Same' }, ...CASES]} value={ls.textCase ?? ''}
                              onPick={(v) => setLine(i, { textCase: (v || undefined) as HookLineStyle['textCase'] })} />
                          </Row>
                          <Row label="Strike-through">
                            <div className="shk-inline">
                              <Toggle on={ls.strike != null} onClick={() => setLine(i, { strike: ls.strike != null ? undefined : '#ff2d2d' })} />
                              {ls.strike != null && (
                                <input type="color" className="shk-color" value={hex(ls.strike, '#ff2d2d')} onChange={(e) => setLine(i, { strike: e.target.value })} />
                              )}
                            </div>
                          </Row>
                          <Slider label="Appears at" min={0} max={Math.max(400, span - 200)} step={20} value={ls.delayMs ?? 0}
                            onChange={(v) => setLine(i, { delayMs: v })} display={`+${secs(ls.delayMs ?? 0)}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {tab === 'style' && (
          <>
            <Row label="Type">
              <Seg options={TYPES.filter((t) => t.v !== 'opening' || !hasOpening || h.type === 'opening')}
                value={h.type} onPick={(v) => set({ type: v })} />
            </Row>
            <Row label="Fill">
              <Seg options={FILLS} value={h.fill ?? 'solid'} onPick={setFill} wrap />
            </Row>
            {!pillOn && h.fill !== 'footage' && (
              <Row label={h.fill === 'metal' ? 'Metal color' : 'Text color'}>
                <input type="color" className="shk-color" value={hex(h.color, '#ffffff')}
                  onChange={(e) => set({ color: e.target.value })} />
              </Row>
            )}
            {usesFootage && (
              <>
                <span className="shk-sublabel">Footage inside the letters</span>
                <Slider label="Zoom" min={1} max={4} step={0.05} value={tex.zoom} onChange={(v) => setTexture({ zoom: v })} display={`${tex.zoom.toFixed(2)}×`} />
                <Slider label="Soften" min={0} max={1} step={0.02} value={tex.blur} onChange={(v) => setTexture({ blur: v })} display={pct(tex.blur)} />
                <Slider label="Brightness" min={-1} max={1} step={0.02} value={tex.brightness} onChange={(v) => setTexture({ brightness: v })}
                  display={`${tex.brightness > 0 ? '+' : ''}${Math.round(tex.brightness * 100)}`} />
                <Slider label="Color" min={0} max={1} step={0.02} value={tex.saturation} onChange={(v) => setTexture({ saturation: v })} display={pct(tex.saturation)} />
                <Row label="Tint">
                  <input type="color" className="shk-color" value={hex(tex.tint, '#ffe600')} onChange={(e) => setTexture({ tint: e.target.value, tintAmount: tex.tintAmount || 0.6 })} />
                </Row>
                <Slider label="Tint amount" min={0} max={1} step={0.02} value={tex.tintAmount} onChange={(v) => setTexture({ tintAmount: v })} display={pct(tex.tintAmount)} />
              </>
            )}
            <Slider label="Opacity" min={0.1} max={1} step={0.02} value={h.opacity ?? 1}
              onChange={(v) => set({ opacity: v >= 0.995 ? undefined : v })} display={pct(h.opacity ?? 1)} />
            <Row label="Pill background">
              <Toggle on={pillOn} onClick={() => set({ bgColor: pillOn ? undefined : '#101216' })} />
            </Row>
            {pillOn && (
              <Row label="Pill color">
                <input type="color" className="shk-color" value={hex(h.bgColor, '#101216')}
                  onChange={(e) => set({ bgColor: e.target.value })} />
              </Row>
            )}
            <Row label="Outline">
              <Toggle on={strokeOn}
                onClick={() => set({ stroke: strokeOn ? undefined : { color: '#ffffff', width: 0.08 } })} />
            </Row>
            {strokeOn && (
              <>
                <Row label="Outline color">
                  <input type="color" className="shk-color" value={hex(h.stroke?.color, '#ffffff')}
                    onChange={(e) => set({ stroke: { color: e.target.value, width: h.stroke?.width ?? 0.08 } })} />
                </Row>
                <Slider label="Outline width" min={0.01} max={0.2} step={0.01} value={h.stroke?.width ?? 0.08}
                  onChange={(v) => set({ stroke: { color: h.stroke?.color ?? '#ffffff', width: v } })}
                  display={`${Math.round((h.stroke?.width ?? 0.08) * 100)}`} />
              </>
            )}
            <Row label="Glow">
              <Toggle on={glowOn}
                onClick={() => set({ glow: glowOn ? undefined : { color: '#22d3ee', blur: 0.22 } })} />
            </Row>
            {glowOn && (
              <>
                <Row label="Glow color">
                  <input type="color" className="shk-color" value={hex(h.glow?.color, '#22d3ee')}
                    onChange={(e) => set({ glow: { color: e.target.value, blur: h.glow?.blur ?? 0.22 } })} />
                </Row>
                <Slider label="Glow size" min={0.05} max={0.6} step={0.01} value={h.glow?.blur ?? 0.22}
                  onChange={(v) => set({ glow: { color: h.glow?.color ?? '#22d3ee', blur: v } })} display={pct(h.glow?.blur ?? 0.22)} />
              </>
            )}
          </>
        )}

        {tab === 'motion' && (
          <>
            <div className="shk-motion-top">
              <span className="str-section">Motion presets</span>
              <button className="shk-mini" onClick={replay}>▶ Replay</button>
            </div>
            <div className="hkm-grid">
              {HOOK_MOTION_PRESETS.map((p) => (
                <button key={p.id} className={motionMatches(p.motion) ? 'hkm on' : 'hkm'} title={p.desc}
                  onClick={() => set({ motion: p.motion })}>
                  <span className={`hkm-demo hkm-${p.id}`}>Aa</span>
                  <span className="hkp-name">{p.name}</span>
                </button>
              ))}
            </div>

            <div className="ses-divider" />
            <span className="str-section">Entrance</span>
            <Row label="Style">
              <Seg options={IN_KINDS} value={motion.in.kind} onPick={pickInKind} wrap />
            </Row>
            {motion.in.kind !== 'none' && (
              <PhaseControls phase={motion.in} onChange={setIn} kind={motion.in.kind} />
            )}

            <div className="ses-divider" />
            <span className="str-section">Exit</span>
            <Row label="Style">
              <Seg options={OUT_KINDS} value={motion.out.kind} onPick={pickOutKind} wrap />
            </Row>
            {motion.out.kind !== 'none' && (
              <PhaseControls phase={motion.out} onChange={setOut} kind={motion.out.kind} />
            )}

            <div className="ses-divider" />
            <span className="str-section">Cascade</span>
            <Row label="Animate by">
              <Seg options={SPLITS} value={motion.split}
                onPick={(v) => setMotion({ split: v, staggerMs: v === 'block' ? 0 : motion.staggerMs || (v === 'char' ? 40 : v === 'word' ? 130 : 300) })} />
            </Row>
            {motion.split !== 'block' && (
              <Slider label="Stagger" min={0} max={motion.split === 'char' ? 200 : 900} step={5} value={motion.staggerMs}
                onChange={(v) => setMotion({ staggerMs: v })} display={`${motion.staggerMs}ms`} />
            )}

            <div className="ses-divider" />
            <span className="str-section">While holding</span>
            <Row label="Motion">
              <Seg options={IDLES} value={motion.idle} onPick={(v) => setMotion({ idle: v })} wrap />
            </Row>
            {motion.idle !== 'none' && (
              <Slider label="Amount" min={0} max={1} step={0.02} value={motion.idleAmount}
                onChange={(v) => setMotion({ idleAmount: v })} display={pct(motion.idleAmount)} />
            )}
          </>
        )}

        {tab === 'place' && (
          <>
            <Slider label="Start" min={0} max={dur} step={10} value={h.startMs}
              onChange={(v) => set({ startMs: v, endMs: Math.max(v + 400, h.endMs) })} display={secs(h.startMs)} />
            <Slider label="Linger" min={h.startMs + 400} max={Math.max(h.startMs + 6000, dur)} step={50} value={h.endMs}
              onChange={(v) => set({ endMs: v })} display={secs(h.endMs - h.startMs)} />
            <Row label="Position">
              <Seg options={ANCHORS} value={h.anchor} onPick={(v) => set({ anchor: v })} />
            </Row>
            <Slider label="Fine Y" min={-0.3} max={0.3} step={0.01} value={h.offsetYNorm ?? 0}
              onChange={(v) => set({ offsetYNorm: v })}
              display={`${(h.offsetYNorm ?? 0) > 0 ? '+' : ''}${Math.round((h.offsetYNorm ?? 0) * 100)}%`} />
            <Slider label="Fine X" min={-0.4} max={0.4} step={0.01} value={h.offsetXNorm ?? 0}
              onChange={(v) => set({ offsetXNorm: Math.abs(v) < 0.005 ? undefined : v })}
              display={`${(h.offsetXNorm ?? 0) > 0 ? '+' : ''}${Math.round((h.offsetXNorm ?? 0) * 100)}%`} />
            <Slider label="Size" min={0.4} max={3} step={0.05} value={h.scale}
              onChange={(v) => set({ scale: v })} display={pct(h.scale)} />
            <Slider label="Tilt" min={-30} max={30} step={1} value={h.rotateDeg ?? 0}
              onChange={(v) => set({ rotateDeg: v === 0 ? undefined : v })} display={`${h.rotateDeg ?? 0}°`} />

            <div className="ses-divider" />
            <Row label="Behind person (3D)">
              <Toggle on={Boolean(h.behind)} onClick={() => toggleBehind(!h.behind)} />
            </Row>
            {h.behind && (
              <p className="shk-hint shk-depth-note">
                {cutout.status === 'ready' ? 'Live behind-person preview is on.'
                  : cutout.status === 'running' ? 'Preparing the person cut-out…'
                    : 'Needs a one-time person cut-out to preview - generate it when prompted.'}
              </p>
            )}
            <Row label="Follow the person">
              <Toggle on={Boolean(h.track)} onClick={() => void toggleTrack(!h.track)} />
            </Row>
            {h.track && (
              <>
                <p className="shk-hint shk-depth-note">
                  {cutout.status !== 'ready'
                    ? 'Tracking uses the person cut-out - generate it when prompted.'
                    : tracking ? 'Tracking the head through the clip…'
                      : cutout.track ? 'Keeps its distance from the head as the camera moves.'
                        : 'No head track yet - toggle off and on to analyze the clip.'}
                </p>
                <Slider label="Follow strength" min={0} max={1} step={0.02} value={h.track.strength}
                  onChange={(v) => set({ track: { ...h.track!, strength: v } })} display={pct(h.track.strength)} />
                <Row label="Scale with zoom">
                  <Toggle on={h.track.scale} onClick={() => set({ track: { ...h.track!, scale: !h.track!.scale } })} />
                </Row>
              </>
            )}
            <Row label="Hide captions">
              <Toggle on={h.hideCaptions} onClick={() => set({ hideCaptions: !h.hideCaptions })} />
            </Row>
          </>
        )}

        <button className="str-remove" onClick={() => removeHook(h.id)}>Remove hook</button>
      </div>
    </aside>
  );
}

